import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';

import { CatchHeatService, CatchHeatState } from '../../../../services/catch-heat.service';
import {
  CatchSpeciesOption, FALLBACK_SPECIES, analysisParagraphs, catchHeatTitle,
} from '../../../../shared/utils/catch-heat.util';

interface SpotSection {
  rank: number | null;
  band: string;
  summary: string;
  isPrimary: boolean;
}

/**
 * On-map catch-heat controls — Flutter's `CatchHeatControls` (timeframe + species) plus the
 * status line and the "Conditions" document. Selection state lives in `CatchHeatService`; this only
 * renders it and reports taps. Chips lock while a fetch is in flight so no second tap lands
 * mid-request.
 */
@Component({
  selector: 'app-catch-heat-controls',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './catch-heat-controls.component.html',
  styleUrl: './catch-heat-controls.component.scss',
})
export class CatchHeatControlsComponent implements OnInit, OnDestroy {
  s!: CatchHeatState;
  showConditions = false;

  private sub?: Subscription;

  constructor(public heat: CatchHeatService, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.sub = this.heat.state$.subscribe((s) => { this.s = s; this.cdr.markForCheck(); });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  get loading(): boolean {
    return this.s.status === 'loading';
  }

  get title(): string {
    return catchHeatTitle(this.s.species, this.speciesOptions);
  }

  /** Window chips come from the response; a single window is noise, so the row hides. */
  get windows(): number[] {
    return this.s.data.availableWindows.length > 1 ? this.s.data.availableWindows : [];
  }

  /** Per-window rows when the backend sends them (no refetch on a window switch), else the flat list. */
  get speciesOptions(): CatchSpeciesOption[] {
    const byWindow = this.s.data.availableSpeciesByWindow[this.s.window];
    const rows = byWindow?.length ? byWindow : this.s.data.availableSpecies;
    return rows.length ? rows : FALLBACK_SPECIES;
  }

  /** "All species" + every category pill; subtypes sit under their category's own label. */
  get topRow(): CatchSpeciesOption[] {
    return this.speciesOptions.filter((o) => o.group == null);
  }

  get groups(): { label: string; rows: CatchSpeciesOption[] }[] {
    const opts = this.speciesOptions;
    const topKeys = new Set(this.topRow.map((o) => o.key));
    const out = this.topRow
      .filter((c) => c.key !== 'all')
      .map((c) => ({ label: c.label, rows: opts.filter((o) => o.group === c.key) }))
      .filter((g) => g.rows.length);
    // Subtypes whose parent row never arrived still render, so nothing served is dropped.
    const orphans = opts.filter((o) => o.group != null && !topKeys.has(o.group));
    return orphans.length ? [...out, { label: 'Other species', rows: orphans }] : out;
  }

  chipLabel(o: CatchSpeciesOption): string {
    return o.count == null ? o.label : `${o.label} (${o.count})`;
  }

  windowLabel(w: number): string {
    return w === 1 ? '1 day' : `${w} days`;
  }

  // ── Conditions panel ─────────────────────────────────────────────────────────

  get paragraphs(): string[] {
    return analysisParagraphs(this.s.data.analysis);
  }

  /** The stitched fallback only when the backend sent no composed document. */
  get sections(): SpotSection[] {
    if (this.paragraphs.length) return [];
    return this.s.data.hotspots
      .map((f) => f.properties ?? {})
      .filter((p) => typeof p['summary'] === 'string' && p['summary'])
      .map((p) => ({ rank: typeof p['rank'] === 'number' ? p['rank'] : null, band: String(p['band'] ?? ''), summary: String(p['summary']), isPrimary: p['is_primary'] === true }))
      .sort((a, b) => (a.rank ?? 1e9) - (b.rank ?? 1e9));
  }

  /** Nothing verbatim to show → don't offer the panel (graceful empty state). */
  get hasConditions(): boolean {
    return this.paragraphs.length > 0 || !!this.s.data.regionalSummary || this.sections.length > 0;
  }

  bandClass(band: string): string {
    const b = band.toLowerCase();
    return b === 'strong' || b === 'moderate' || b === 'emerging' ? `band-${b}` : 'band-other';
  }
}
